<template>
  <div class="agency-picker">
    <el-select
      class="agency-picker__select"
      :value="value"
      :multiple="multiple"
      filterable
      remote
      reserve-keyword
      clearable
      :disabled="disabled || !!denied"
      :remote-method="search"
      :loading="loading"
      :placeholder="denied || placeholder || text.agencyNmLabel"
      @focus="onFocus"
      @change="onChange">
      <el-option v-for="option in options" :key="option.id" :label="option.label" :value="option.id"></el-option>
    </el-select>
    <div v-if="denied" class="agency-picker__hint">{{ denied }}</div>
  </div>
</template>

<script>
import { canRead, permittedRead } from '@/plugins/http/permittedRead'
import { READ_DENIED, deniedRead } from '@/plugins/http/policy'
import { ROLE_TEXT } from '@/utils/adminRole'
import {
  AGENCY_LOOKUP_READ,
  agencyOptionsOf,
  hasUnresolvedSelection,
  itemsOf,
  keepSelectedOptions,
} from '@/utils/callpopOrg'
import { CALLPOP_TEXT } from '@/utils/callpopText'
import { latestGuard } from '@/utils/latestGuard'

/**
 * **관리점 고르기 한 곳** — 명칭 검색과 코드 해석을 서버(`GET /callpop/agencies/search`)가 함께 답한다(결정 23).
 *
 * 세 자리가 이것을 공유한다: 계정 발급 화면의 AGENCY 축(복수) · 신규 매장 등록의 「등록할 관리점」(단수) ·
 * 매장 매핑 다이얼로그의 목적지(단수). 각자 원격 검색을 적으면 **코드 해석의 뜻**(코드로 읽히면 그 한 행만)이
 * 화면마다 달라지고, 그때 사람이 고르는 대상이 갈린다.
 *
 * 그 검색은 **활성 관리점만** 준다(R-A §1-1) — 고를 대상을 내는 자리라서다. **지난 조직을 지목해야 하는 자리**
 * (변경 이력)는 [source] 로 목록 API(`AGENCY_LIST_READ`, 비활성 포함)를 준다. 코드 해석은 없고 부분 일치다.
 *
 * - 정규화·유일성은 **적지 않는다** — 질의어를 코드로 읽는 것은 서버뿐이고 화면은 그 답을 보인다.
 * - 액션(`CallpopAgencyLookup`)이 없으면 **부르지 않고** 읽기 전용이 된다. 리소스 없는 호출은 401 이고
 *   그 401 이 세션을 끊는다(서버 보고서 §10 3).
 * - 이미 고른 값은 후보에서 사라져도 남긴다([keepSelectedOptions]) — 안 그러면 태그가 PK 숫자가 된다.
 */
export default {
  name: 'AgencyPicker',

  props: {
    /** `multiple` 이면 id 문자열 배열, 아니면 id 문자열 하나(`v-model`) */
    value: { type: [Array, String, Number], default: null },
    multiple: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: '' },
    /**
     * 이 운영사 아래 관리점만 고르게 한다 — **권한이 아니라 화면 좁히기**다(서버가 범위를 이미 좁혔다).
     * 매핑 목적지에서 다른 운영사의 관리점을 고르면 서버가 400 으로 답하므로 먼저 보이지 않게 한다.
     */
    operatorCd: { type: [Number, String], default: null },
    /** 후보에서 뺄 id — 고를 수 없는 것(자기 자신)을 보이지 않는다 */
    excludeId: { type: [Number, String], default: null },
    /**
     * 열기 전에 후보를 **미리 읽는다** — 부모가 「몇 개인가」를 알아야 할 때다.
     *
     * 기본은 거짓이다(사람이 열 때 읽는다 — 화면마다 요청이 하나씩 늘지 않게). 신규 매장 등록만 참인데,
     * 그 화면은 「후보가 하나면 자기 관리점 고정」을 진입 시점에 판정해야 하고 그 판정의 재료가
     * **서버가 준 범위**이기 때문이다(역할을 묻지 않는다).
     */
    preload: { type: Boolean, default: false },
    /**
     * 후보를 읽는 조회 spec(`permittedRead` 의 입력) — 기본은 검색(`AGENCY_LOOKUP_READ`, 활성만).
     * 게이트([denied])와 조회([load])가 **같은 spec** 을 본다.
     */
    source: { type: Object, default: () => AGENCY_LOOKUP_READ },
  },

  data() {
    return {
      text: CALLPOP_TEXT,
      options: [],
      loading: false,
      /** 못 읽은 이유 — 빈 문자열이면 정상이다 */
      readDenied: '',
      /** 늦게 온 응답이 최신 질의어의 후보를 덮지 못하게(`utils/latestGuard`) */
      guard: latestGuard(),
      /**
       * **이름을 찾아 보려고 한 id** — 조회가 끝난 뒤 그때 고려 중이던 선택을 적는다.
       *
       * `agencies/search` 는 **활성 관리점만** 준다(서버 §3-3). 비활성 관리점에 묶인 계정을 열면 그 id 는
       * 어떤 조회로도 이름을 얻지 못하는데, 「이름을 아는가」만 물으면 그 사실을 모른 채 **포커스마다
       * 다시 읽는다**. 물어야 할 것은 「아직 **물어보지 않은** 것이 있는가」다 — 한 번 물어본 id 는
       * 답이 없었어도 다시 묻지 않는다(태그는 PK 로 남고, 그것이 지금 알 수 있는 전부다).
       */
      tried: [],
    }
  },

  computed: {
    /** 부를 수 없는 조회인가 — 게이트는 `permittedRead` 와 **같은 술어**([canRead])를 읽는다 */
    denied() {
      if (!canRead(this.source)) return ROLE_TEXT.scopeReadDenied

      return this.readDenied
    },
    selectedIds() {
      if (this.multiple) return Array.isArray(this.value) ? this.value.map(String) : []

      return this.value == null || this.value === '' ? [] : [String(this.value)]
    },
  },

  watch: {
    // 운영사가 바뀌면 후보 집합이 달라진다 — 앞 운영사의 후보를 남기면 고를 수 없는 것을 고르게 된다
    operatorCd() {
      this.reset()
      if (!this.denied) this.load('')
    },
    /**
     * **부모가 바인딩을 갈아 끼웠다** — 계정 다이얼로그는 `destroy-on-close` 없이 살아 있어, 다른 계정을
     * 연달아 열면 이 컴포넌트는 그대로인 채 `value` 만 바뀐다. 앞 계정의 후보가 남아 있으므로 새 값의
     * 이름을 모르고, 태그가 PK 숫자로 남는다 — [shouldLoad] 가 그것을 묻고 다시 읽는다.
     */
    value() {
      if (this.shouldLoad(false)) this.load('')
    },
  },

  created() {
    this.shouldLoad(false) && this.load('')
  },

  beforeDestroy() {
    if (this._timer) clearTimeout(this._timer)
    this.guard.close()
  },

  methods: {
    onChange(value) {
      this.$emit('input', value)
      this.$emit('change', value, this.optionOf(value))
    },

    /** 고른 값의 옵션 — 호출부가 운영사·코드를 함께 알아야 할 때가 있다(매핑 목적지) */
    optionOf(value) {
      if (this.multiple) return (Array.isArray(value) ? value : []).map((id) => this.findOption(id)).filter(Boolean)

      return this.findOption(value)
    },

    findOption(id) {
      return this.options.find((option) => option.id === String(id)) || null
    },

    /**
     * **후보를 읽어야 하는가** — 열기 전(`created`·`value` 변경)과 열 때(`focus`)가 같은 질문을 한다.
     *
     * 종전에는 자리마다 다른 조건을 적었고(`created` 는 「고른 값이 있나」, `onFocus` 는 「후보가 비었나」),
     * 그래서 **후보는 남아 있는데 그 값의 이름을 모르는** 상태를 아무도 묻지 않았다. 물어야 할 것 하나는
     * [hasUnresolvedSelection] 이고, 그 입력은 「후보 ∪ **이미 물어본 id**」다([tried]) — 답이 없을 수
     * 있는 물음을 무한히 되풀이하지 않는다.
     *
     * @param {boolean} opening 사람이 지금 목록을 여는가
     */
    shouldLoad(opening) {
      if (this.denied || this.loading) return false
      // 아직 물어보지 않은 선택이 있다 — 태그가 PK 숫자로 남는 그 상태다
      if (hasUnresolvedSelection(this.selectedIds, this.options.concat(this.tried))) return true
      // 보여 줄 후보가 없다 — 원격 검색은 입력이 있어야 돌아 빈 목록만 보인다
      return this.options.length === 0 && (opening || this.preload)
    },

    /**
     * **부모가 이 선택기를 다른 대상에 다시 쓴다** — 읽던 것을 버리고 처음 상태로 돌린다.
     *
     * 조회가 날고 있는 사이에 `value` 가 바뀌면 워처는 `loading` 때문에 건너뛰고, 그 뒤 도착한 응답이
     * **앞 대상의 후보**로 목록을 채운다. 그 상태를 푸는 것은 값을 바꾸는 쪽(경계를 아는 곳)의 일이다 —
     * 계정 화면의 `resetScope` 가 PLACE 축을 다루는 줄과 같은 자리에서 부른다.
     */
    reset() {
      this.options = []
      this.tried = []
      this.readDenied = ''
      this.loading = false
      this.guard.invalidate()
    },

    onFocus() {
      if (this.shouldLoad(true)) this.load('')
    },

    search(query) {
      if (this._timer) clearTimeout(this._timer)
      this._timer = setTimeout(() => this.load(query), 300)
    },

    /**
     * 후보 조회 — **호출부는 `await` 하지 않아도 된다**: 403 은 값으로 접고([permittedRead]) 그 밖의 실패는
     * 여기서 끝낸다(인터셉터가 이미 토스트로 말했다).
     */
    async load(query) {
      const kept = this.options.filter((option) => this.selectedIds.indexOf(option.id) > -1)
      const seq = this.guard.begin()
      this.loading = true

      try {
        const outcome = await permittedRead(Object.assign({}, this.source, { params: { q: query || '' } }))
        if (!this.guard.isLatest(seq)) return outcome

        if (!outcome.allowed) {
          this.readDenied = outcome.message || ROLE_TEXT.scopeReadDenied
          this.options = kept

          return outcome
        }

        this.readDenied = ''
        this.options = keepSelectedOptions(
          kept,
          this.withinOperator(agencyOptionsOf(itemsOf(outcome.data))),
          this.selectedIds
        )
        // 이 조회가 답한 범위를 적는다 — 못 찾은 id 를 다시 묻지 않는 근거다([tried])
        this.tried = this.tried
          .concat(this.selectedIds.map((id) => ({ id })))
          .filter((row, index, all) => all.findIndex((other) => other.id === row.id) === index)
        // **후보가 몇이고 무엇인가**를 부모가 알아야 할 때가 있다 — 관리점 계정에게는 자기 1행만 오므로
        // 신규 매장 등록 화면이 그 사실로 「자기 관리점 고정」을 판정한다(역할을 묻지 않고 서버 범위를 읽는다)
        this.$emit('loaded', this.options)

        return outcome
      } catch (error) {
        if (this.guard.isLatest(seq)) this.options = kept

        return deniedRead(READ_DENIED.FAILED)
      } finally {
        if (this.guard.isLatest(seq)) this.loading = false
      }
    },

    /**
     * 후보 좁히기 — 운영사 밖과 뺄 id 를 지운다. **권한이 아니라 화면 좁히기**다(서버가 범위를 이미 좁혔다).
     */
    withinOperator(options) {
      const exclude = this.excludeId == null || this.excludeId === '' ? null : String(this.excludeId)
      const byOperator =
        this.operatorCd == null || this.operatorCd === ''
          ? options
          : options.filter((option) => String(option.operatorCd) === String(this.operatorCd))

      return exclude ? byOperator.filter((option) => option.id !== exclude) : byOperator
    },
  },
}
</script>

<style scoped>
.agency-picker__select {
  width: 100%;
}
.agency-picker__hint {
  color: #909399;
  font-size: 12px;
  line-height: 1.6;
  margin-top: 4px;
}
</style>
